/**
 * Fleet document — one tail list for every desk, sync, and companion reader.
 *
 * Rust `fleet_apply` is the desktop owner and emits `fleet://changed`.
 * Required figures and weight order live in Rust (`fleet_validate_tail`).
 * This module migrates and cleans the list the store reads back.
 */
import { hydrateAircraft } from "./maintenance";
import type { AircraftSpec, OpsDeskSettings } from "./types";
import type { WeightBalanceDesk } from "./weightBalance";

export const FLEET_APPLY_COMMAND = "fleet_apply";
export const FLEET_VALIDATE_COMMAND = "fleet_validate_tail";
export const FLEET_CHANGED_EVENT = "fleet://changed";

export type FleetTailCheck = { ok: true } | { ok: false; error: string };

/**
 * Persist migration. Existing tails keep every number already on file.
 * Missing engine / APU hours follow the same hydrate the desk already used.
 * An empty or missing list stays empty — this does not invent a starter fleet.
 */
export function migrateFleetDocument(raw: unknown): AircraftSpec[] {
  if (!Array.isArray(raw)) return [];
  const out: AircraftSpec[] = [];
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    out.push(hydrateAircraft(row as AircraftSpec));
  }
  return out;
}

/** The fleet array on a snapshot. Missing means none — never a catalog stand-in. */
export function readLiveFleet<T>(fleet: readonly T[] | null | undefined): T[] {
  return Array.isArray(fleet) ? [...fleet] : [];
}

/**
 * Persisted tails, including an empty list. A missing field stays empty.
 * This does not invent Hawker or PC-12 rows.
 */
export function liveFleetFromPersist(raw: unknown): AircraftSpec[] {
  return migrateFleetDocument(raw);
}

/**
 * Drop W&B profiles and tail cost rows for tails that are no longer on the
 * fleet. A type cost row is removed only when the last tail of that ICAO
 * was one of `removedTails` and no remaining tail still uses it.
 */
export function cleanupRemovedTails(input: {
  fleet: readonly Pick<AircraftSpec, "id" | "icaoType">[];
  removedTails: readonly Pick<AircraftSpec, "id" | "icaoType">[];
  weightBalance: WeightBalanceDesk;
  opsDesk: OpsDeskSettings;
}): { weightBalance: WeightBalanceDesk; opsDesk: OpsDeskSettings } {
  const liveIds = new Set(input.fleet.map((tail) => tail.id));
  const liveTypes = new Set(input.fleet.map((tail) => tail.icaoType.trim().toUpperCase()).filter(Boolean));
  const removedTypes = new Set(
    input.removedTails.map((tail) => tail.icaoType.trim().toUpperCase()).filter(Boolean),
  );
  const profiles: WeightBalanceDesk["profiles"] = {};
  for (const [id, profile] of Object.entries(input.weightBalance.profiles)) {
    if (liveIds.has(id)) profiles[id] = profile;
  }
  const selected = liveIds.has(input.weightBalance.selectedAircraftId)
    ? input.weightBalance.selectedAircraftId
    : (input.fleet[0]?.id ?? "");
  const aircraftCosts = input.opsDesk.aircraftCosts.filter((row) => {
    if (liveTypes.has(row.icaoType)) return true;
    return !removedTypes.has(row.icaoType);
  });
  return {
    weightBalance: { selectedAircraftId: selected, profiles },
    opsDesk: {
      ...input.opsDesk,
      aircraftCosts,
      tailCostOverrides: input.opsDesk.tailCostOverrides.filter((row) => liveIds.has(row.aircraftId)),
    },
  };
}
